import React from 'react';
import {
  Amount,
  LiquidityAmount,
  TokenAmount,
  PercentAmount,
  roundWei,
} from '@akropolis-web/primitives';
import { styled, Tooltip } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';

import { isMobileDevice } from 'utils/isMobileDevice';
import { toEither } from 'utils/either';
import { MAX_UINT_256 } from 'utils/bn';
import { getErrorMsg } from 'utils/getErrorMsg';

import { getIsAmountTheSame } from './getIsAmountTheSame';
import { FormattedAmountProps } from './types';
import { PercentAmountComponent } from './PercentAmountComponent';
import { LiquidityOrTokenAmountComponent } from './LiquidityOrTokenAmountComponent';

const calculateColor = (sum: Amount, theme: Theme) => {
  if (sum.isZero()) {
    return 'inherit';
  }
  if (sum.lt(0)) {
    return theme.palette.amount.negative;
  }
  return theme.palette.amount.positive;
};

const PERCENT_PRECISION = 5;
const ROUNDED_AMOUNT_PRECISION = 8;

export function FormattedAmount<A extends Amount>(props: FormattedAmountProps<A>) {
  const {
    sum: originSum,
    precision,
    withSI,
    hasSign = false,
    symbolVariant,
    symbolSize,
    tooltip = 'default',
    wrap,
    siPrecision,
    renderSymbol,
    rootSx,
    colored = false,
  } = props;

  const theme = useTheme();

  return toEither(originSum).fold<JSX.Element>(
    amount => (amount.gte(MAX_UINT_256) ? <>Infinity</> : renderSum(amount)),
    error => renderNA(getErrorMsg(error)),
  );

  function renderSum(sum: Amount) {
    const formattedBalance = withSI
      ? sum.toFormattedString(precision, symbolVariant !== 'none')
      : sum.toShortString(undefined, false);

    const notRoundedBalance = sum.toFormattedString(
      sum instanceof PercentAmount ? PERCENT_PRECISION : sum.currency.decimals,
    );

    const isFormattedNumberTheSame = getIsAmountTheSame(
      notRoundedBalance,
      formattedBalance,
      withSI,
    );

    return sum.isZero() ||
      tooltip === 'none' ||
      (tooltip === 'default' && isFormattedNumberTheSame) ? (
      <Root color={colored ? calculateColor(sum, theme) : 'inherit'} sx={rootSx}>
        {renderAmount()}
      </Root>
    ) : (
      <Tooltip
        arrow={false}
        title={tooltip === 'default' ? notRoundedBalance : tooltip}
        disableTouchListener={isMobileDevice()}
        disableHoverListener={isMobileDevice()}
      >
        <Root color={colored ? calculateColor(sum, theme) : 'inherit'} sx={rootSx}>
          {renderAmount()}
        </Root>
      </Tooltip>
    );

    function renderAmount() {
      if (sum instanceof PercentAmount) {
        return (
          <PercentAmountComponent
            {...{
              sum,
              withSI,
              siPrecision,
              precision,
              symbolSize,
              hasSign,
              renderSymbol: renderSymbol as FormattedAmountProps<PercentAmount>['renderSymbol'],
            }}
          />
        );
      }

      if (sum instanceof LiquidityAmount || sum instanceof TokenAmount) {
        return (
          <LiquidityOrTokenAmountComponent
            {...{
              sum: getRoundedSum(sum),
              withSI,
              siPrecision,
              precision,
              symbolVariant,
              symbolSize,
              hasSign,
              wrap,
              renderSymbol: renderSymbol as FormattedAmountProps<
                LiquidityAmount | TokenAmount
              >['renderSymbol'],
            }}
          />
        );
      }

      return formattedBalance;
    }
  }

  function renderNA(error: string) {
    return (
      <Tooltip
        arrow={false}
        title={error}
        disableTouchListener={isMobileDevice()}
        disableHoverListener={isMobileDevice()}
      >
        <span>N/A</span>
      </Tooltip>
    );
  }
}

function getRoundedSum<A extends Amount>(sum: A): A {
  return sum.withValue(
    roundWei(sum.toBN(), sum.currency.decimals, 'half-away-from-zero', ROUNDED_AMOUNT_PRECISION),
  );
}

const Root = styled('span')(({ color }) => ({
  color,
}));
